import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Badge, Text, Tooltip } from '@mantine/core'
import { DocumentDetail } from './DocumentDetail'
import { DocumentsTable } from './DocumentsTable'
import { useCpgSources, type CpgSourceSummary } from './hooks'

function SourceCard({ source }: { source: CpgSourceSummary }) {
  const c = source.counts
  const lastSync = source.lastSyncAt ? new Date(source.lastSyncAt).toLocaleString() : 'never'
  return (
    <div className="shrink-0 rounded border border-gray-200 px-3 py-1.5">
      <Text size="sm" fw={600} component="span">
        {source.name}
      </Text>
      <Text size="xs" c="dimmed" component="span" className="ml-2">
        {source.licenseClass} · synced {lastSync}
      </Text>
      <div className="mt-1 flex items-center gap-1">
        <Tooltip label="pending classification">
          <Badge size="sm" variant="light" color="gray">
            {c.pendingClassification}
          </Badge>
        </Tooltip>
        <Tooltip label="needs registration">
          <Badge size="sm" variant="light" color="blue">
            {c.needsRegistration}
          </Badge>
        </Tooltip>
        <Tooltip label="active">
          <Badge size="sm" variant="light" color="green">
            {c.active}
          </Badge>
        </Tooltip>
        <Tooltip label="superseded">
          <Badge size="sm" variant="light" color="yellow">
            {c.superseded}
          </Badge>
        </Tooltip>
        <Tooltip label="withdrawn">
          <Badge size="sm" variant="light" color="red">
            {c.withdrawn}
          </Badge>
        </Tooltip>
      </div>
    </div>
  )
}

/** The full corpus browser: per-source summary strip, all documents, read-mostly detail. */
export function DocumentsView() {
  const [searchParams, setSearchParams] = useSearchParams()
  const docParam = searchParams.get('doc')
  const parsed = docParam === null ? NaN : Number(docParam)
  const selectedId = Number.isInteger(parsed) && parsed > 0 ? parsed : null
  // ?unit=<id> highlights one passage — how a measure's citation links into its document.
  const unitParam = Number(searchParams.get('unit'))
  const highlightUnitId = Number.isInteger(unitParam) && unitParam > 0 ? unitParam : null
  const [reloadKey, setReloadKey] = useState(0)
  const sources = useCpgSources()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-gray-200 px-3 py-2">
        {(sources.data?.data ?? []).map((source) => (
          <SourceCard key={source.id} source={source} />
        ))}
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="w-[calc(55%+75px)] min-w-0 border-r border-gray-200">
          <DocumentsTable
            scope="all"
            reloadKey={reloadKey}
            onSelect={(id) => setSearchParams({ doc: String(id) }, { replace: true })}
          />
        </div>
        <div className="w-[calc(45%-75px)] min-w-0">
          <DocumentDetail
            id={selectedId}
            highlightUnitId={highlightUnitId}
            onMutated={() => setReloadKey((k) => k + 1)}
          />
        </div>
      </div>
    </div>
  )
}
